import { z, EnumLike } from 'zod'
import {
  formatDateForApi,
  formatDateTimeForApi,
  fromDataTimeStringApi,
  fromDateStringApi,
} from '~/shared/lib/date'
import { sanitizePhoneNumber, sanitizePriceNumber } from '~/shared/lib/utils'

export const dateSchema = z
  .preprocess((arg) => {
    if (typeof arg == 'string') {
      return fromDateStringApi(arg)
    }
    return arg
  }, z.date())
  .transform(formatDateForApi)

export const dateTimeSchema = z
  .preprocess((arg) => {
    if (arg === null || arg === undefined || arg === '') {
      return null
    }
    if (typeof arg === 'string') {
      return fromDataTimeStringApi(arg)
    }
    if (arg instanceof Date) {
      return arg
    }
    return arg
  }, z.date().nullable())
  .transform((val) => {
    if (val === null || val === undefined) {
      return null
    }
    return formatDateTimeForApi(val)
  })

export const phoneScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = sanitizePhoneNumber(value)
    if (sanitized.length > 0 && sanitized.length < 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Неверный номер телефона',
      })
    }
  })
  .transform(sanitizePhoneNumber)

export const phonesArraySchema = z
  .array(z.string())
  .transform((phones) =>
    phones
      .map((phone) => sanitizePhoneNumber(phone).replace(/^\+/, ''))
      .filter((p) => p.length > 0),
  )
  .superRefine((phones, ctx) => {
    phones.forEach((phone, index) => {
      if (phone.length > 0 && phone.length < 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Неверный номер телефона: ${phone}`,
          path: [index],
        })
      }
    })
  })
  .optional()

export const priceRequiredScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizePriceNumber(value)
    if (!sanitized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
    if (sanitized > 999999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Превышена допустимая стоимость',
      })
    }
  })
  .transform(sanitizePriceNumber)

export const priceRequiredWithZeroScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizePriceNumber(value)
    if (!sanitized && sanitized !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обязательное поле',
      })
    }
    if (sanitized > 999999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Превышена допустимая стоимость',
      })
    }
  })
  .transform(sanitizePriceNumber)

export const optionSchema = z.object({
  id: z.string(),
  label: z.string(),
})
export const uuidOptionSchema = z
  .union([z.string().uuid(), optionSchema])
  .transform((arg) => {
    if (typeof arg === 'string') {
      return arg
    }
    return arg.id
  })
export const uuidOptionsSchema = z.array(uuidOptionSchema)

export const enumOptionSchema = <T extends EnumLike>(values: T) =>
  z.union([z.nativeEnum(values), optionSchema]).transform((arg) => {
    if (typeof arg === 'object' && 'id' in arg) {
      return arg.id
    }
    return arg
  })

export const enumOptionLabelSchema = <T extends EnumLike>(values: T) =>
  z.union([z.nativeEnum(values), optionSchema]).transform((arg) => {
    if (typeof arg === 'object' && 'label' in arg) {
      return arg.label
    }
    return arg
  })

export const yearSchema = z
  .union([
    z
      .string()
      .regex(
        /^\d{4}$/,
        'Год имеет неправильный формат, ожидается, например "2022"',
      ),
    z.number(),
  ])
  .transform((arg) => {
    if (typeof arg === 'string') return parseInt(arg)
    return arg
  })
const yearsSeparatedByCommasRegex = /\b\d{4}\b(?:,\s*\b\d{4}\b)*/g
export const yearsSeparatedByCommasSchema = (min = 0) =>
  z
    .union([
      z
        .string()
        .regex(
          yearsSeparatedByCommasRegex,
          'Года имеют неправильный формат, ожидается, например "1999,2004,2022"',
        ),
      z.array(yearSchema).min(min),
    ])
    .transform((arg) => {
      if (typeof arg === 'string') {
        return arg.match(/\d{4}/g)?.map(Number)
      }
      return arg
    })

export const numberRequiredScheme = z.coerce
  .number({
    invalid_type_error: 'Обязательное поле',
  })
  .min(0.01, 'Обязательное поле')

export const stringRequiredScheme = z
  .string()
  .trim()
  .min(1, 'Обязательное поле')

export const okpoRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{8}$|^\d{10}$/, 'Только цифры, длина ОКПО - 8 или 10 символов')

export const innRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{10}$|^\d{12}$/, 'Только цифры, длина ИНН - 10 или 12 символов')

export const kppRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина КПП - 9 символов',
  )

export const ogrnRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{13}$/.test(value),
    'Только цифры, длина ОГРН - 13 символов',
  )

export const bankRsRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{20}$/.test(value),
    'Только цифры, длина поля - 20 символов',
  )

export const bankBikRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    'Только цифры, длина БИК - 9 символов',
  )

export const detailsSchema = z
  .object({
    discount_percent: z.coerce.number().optional(),
    discount_amount: z.coerce.number().optional(),
    duration_days: z.coerce.number().optional(),
  })
  .superRefine((data, ctx) => {
    const hasPercent =
      data.discount_percent !== undefined &&
      data.discount_percent !== null &&
      !isNaN(data.discount_percent) &&
      data.discount_percent > 0

    const hasAmount =
      data.discount_amount !== undefined &&
      data.discount_amount !== null &&
      !isNaN(data.discount_amount) &&
      data.discount_amount > 0

    if (!hasPercent && !hasAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Необходимо указать либо процент скидки, либо сумму скидки',
        path: ['discount_percent'],
      })
      return
    }

    if (hasPercent && hasAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Нельзя указать одновременно процент и сумму скидки',
        path: ['discount_percent'],
      })
      return
    }

    if (hasPercent) {
      const percent = data.discount_percent
      if (
        percent !== undefined &&
        percent !== null &&
        (percent <= 0 || percent > 100)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Процент скидки должен быть от 1 до 100',
          path: ['discount_percent'],
        })
      }
    }

    if (hasAmount) {
      const amount = data.discount_amount
      if (amount !== undefined && amount !== null && amount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Сумма скидки должна быть больше 0',
          path: ['discount_amount'],
        })
      }
    }

    if (data.duration_days === undefined || data.duration_days === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Необходимо указать количество дней',
        path: ['duration_days'],
      })
    } else if (data.duration_days < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Количество дней должно быть не менее 1',
        path: ['duration_days'],
      })
    }
  })

export const nullableNumberSchema = z
  .union([z.string(), z.number()])
  .transform((val) => {
    if (val === '' || val === null || val === undefined) return null
    return typeof val === 'string' ? Number(val) : val
  })
  .optional()
  .nullable()
